import { Injectable } from '@angular/core';
import { YearService } from '../year/year.service';
import { UserPermission } from '../user/user-permission.model';
import { UserService } from '../user/user.service';
import { BehaviorSubject, Observable} from 'rxjs';
import { PermissionEnum } from './permissions.enums';
import {PermissionGroup} from './permission-group.model';
import {HttpClient} from '@angular/common/http';
import {System} from '../System';
import { uniq } from 'lodash';

@Injectable()
export class SecurityService {

    private _userPermissions: UserPermission[] = [];

    public get userPermissions() {
        return this._userPermissions;
    }

    public set userPermissions(permissions: UserPermission[]) {
        this._userPermissions = permissions;
        this.observablePermissions.next(this._userPermissions);
    }

    public observablePermissions = new BehaviorSubject<UserPermission[]>([]);
    observablePermissionStream$ = this.observablePermissions.asObservable();

    get userPermissionGroupIDs(): number[] {
    return uniq(this._userPermissions.map(o => o.GroupId));
    }


    constructor(
        private yearService: YearService,
        private userService: UserService,
        private http: HttpClient
    ) {

        // If the academic year or school changes, load the user permission as per newly selected year/school
        this.yearService.observableDefaultYearStream$.subscribe(defaultYear => {
            this.userPermissions = [];
            this.loadUserPermissions();
        });
    }

    loadUserPermissions() {
        if (this.userService.currentUser != null && this.userService.currentUser.UserType == 1 && this.userPermissions.filter(p => p.SiteId === this.yearService.defaultYear.SchoolID).length == 0) {
            this.userService.getUserPermissions(this.yearService.defaultYear.Id, this.yearService.defaultYear.SchoolID).subscribe(permissions => {
                this.userPermissions = [...this.userPermissions, ...permissions];
                this.observablePermissions.next(this.userPermissions);
            });
        }
    }

    // permission checking
    // return true if any of the supplied permission is assigned to the logged-in user.
    IsAnyPermissionAssigned(...permissions: PermissionEnum[]): boolean {
        return permissions.some(p => this.userPermissions.findIndex(x => x.SiteId === this.yearService.defaultYear.SchoolID && x.PrivilegeId == p) >= 0);
    }

    // return true if all supplied permissions must be assigned to the logged-in user.
    IsAllPermissionAssigned(...permissions: PermissionEnum[]): boolean {
        return permissions.every(p => this.userPermissions.findIndex(x => x.SiteId === this.yearService.defaultYear.SchoolID && x.PrivilegeId == p) >= 0);
    }

    // return true if use has given permission with given special value
    IsPermissionCategoryAssigned(permissionCode: string, category: string) {
        return this.userPermissions.findIndex(o => o.PermissionCode == permissionCode && o.PermissionCategory == category) >= 0;
    }

    // return true if any of the supplied permission codes is assigned to the logged-in user.
    IsAnyPermissionCodeAssigned(...permissions: string[]): boolean {
        return permissions.some(p => this.userPermissions.findIndex(x => x.PermissionCode == p) >= 0);
    }

    // return true if all supplied permission codes must be assigned to the logged-in user.
    IsAllPermissionCodeAssigned(...permissions: string[]): boolean {
        return permissions.every(p => this.userPermissions.findIndex(x => x.PermissionCode == p) >= 0);
    }

    // return true if any of the supplied permission group IDs is assigned to the logged-in user.
    IsAnyPermissionGroupAssigned(...permissionGroupIDs: number[]): boolean {
        return permissionGroupIDs.some(p => this.userPermissions.findIndex(x => x.GroupDescriptionId == p) >= 0);
    }

    // return true if all supplied permission group IDs are assigned to the logged-in user.
    IsAllPermissionGroupAssigned(...permissionGroupIDs: number[]): boolean {
        return permissionGroupIDs.every(p => this.userPermissions.findIndex(x => x.GroupDescriptionId == p) >= 0);
    }

    getSchoolIdsFromPermissions(permissions: number[]): number[] {
        const distinct = (value, index, self) => {
            return self.indexOf(value) === index;
        };
        const schoolIds = this.userPermissions.filter(o => permissions.includes(o.PrivilegeId) ).map(o => o.SiteId);
        return schoolIds.filter(distinct);
    }

    getPermissionGroups(): Observable<PermissionGroup[]> {
        return this.http.get<PermissionGroup[]>(System.apiURL.Admin + 'GetPermissionGroups');
    }
}
